// StatusBar.js - 하단 작업 상태 표시줄
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiDatabase, FiX, FiFolder
} from 'react-icons/fi';
import { useTasks } from '../contexts/TaskContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useProject } from '../contexts/ProjectContext';
import { useDBConnection } from '../contexts/DBConnectionContext';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.BASE;
const DB_CONNECTION_TOGGLE_KEY = 'otl_db_connection_toggle'; // 우측 사이드바와 동일한 키

function StatusBar() {
  const { 
    tasks
  } = useTasks();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const { currentProject } = useProject();
  const { isConnected: dbContextConnected, serverInfo: dbContextServerInfo } = useDBConnection();
  // DBConnectionContext의 연결 상태를 우선 사용, 없으면 기존 로직 사용
  const [isDbConnected, setIsDbConnected] = useState(dbContextConnected);
  const [dbServerInfo, setDbServerInfo] = useState(dbContextServerInfo);
  const [connectionInfo, setConnectionInfo] = useState({
    server: '',
    port: 3306,
    database: '',
    username: '',
  });

  // 관리자 페이지에서 설정한 DB 연결 정보 가져오기
  const loadDbConnectionInfo = useCallback(async () => {
    try {
      // 백엔드 API로 관리자 페이지에서 설정한 DB 연결 정보 가져오기
      const response = await axios.get(`${apiConfig.V1}/admin/db/config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      
      if (response.data && response.data.database) {
        const info = {
          server: response.data.host || 'localhost',
          port: response.data.port || 3306,
          database: response.data.database || '',
          username: response.data.username || '',
        };
        setConnectionInfo(info);
        return info;
      }
    } catch (error) {
      // API 실패 시 localStorage에서 로드 시도
      try {
        const saved = localStorage.getItem('db_connection_info');
        if (saved) {
          const parsed = JSON.parse(saved);
          const info = {
            server: parsed.host || parsed.server || 'localhost',
            port: parsed.port || 3306,
            database: parsed.database || '',
            username: parsed.username || '',
          };
          setConnectionInfo(info);
          return info;
        }
      } catch (e) {
        // localStorage도 실패하면 기본값 유지
        console.error('DB 연결 정보 로드 실패:', e);
      }
    }
    return null;
  }, []);

  useEffect(() => {
    loadDbConnectionInfo();
  }, [loadDbConnectionInfo]);

  // DB 연결 정보 변경 이벤트 감지 (관리자 페이지에서 DB 연결 설정 변경 시)
  useEffect(() => {
    const handleDbConnectionChanged = async (event) => {
      const detail = event.detail || {};
      if (detail.connected && detail.database) {
        // 연결 정보가 이벤트에 포함되어 있으면 사용
        // server 문자열에서 host:port 형식 파싱
        const serverStr = detail.server || detail.host || 'localhost';
        const serverParts = serverStr.split(':');
        const host = serverParts[0] || 'localhost';
        const port = serverParts[1] ? parseInt(serverParts[1]) : (detail.port || 3306);
        
        setConnectionInfo({
          server: host,
          port: port,
          database: detail.database || '',
          username: detail.username || '',
        });
      } else {
        // 이벤트에 정보가 없으면 API로 다시 가져오기
        await loadDbConnectionInfo();
      }
    };

    const handleDbChanged = async () => {
      // DB 변경 이벤트 발생 시 연결 정보 다시 로드
      await loadDbConnectionInfo();
    };

    window.addEventListener('dbConnectionChanged', handleDbConnectionChanged);
    window.addEventListener('dbChanged', handleDbChanged);
    
    return () => {
      window.removeEventListener('dbConnectionChanged', handleDbConnectionChanged);
      window.removeEventListener('dbChanged', handleDbChanged);
    };
  }, [loadDbConnectionInfo]);

  // DBConnectionContext의 연결 상태와 동기화 (우선 사용)
  useEffect(() => {
    // DBConnectionContext의 상태를 우선 사용
    setIsDbConnected(dbContextConnected);
    setDbServerInfo(dbContextServerInfo);
    
    // DBConnectionContext의 serverInfo가 있으면 그것을 우선 사용
    if (dbContextServerInfo && dbContextServerInfo.database) {
      // server 문자열에서 host:port 형식 파싱
      const serverParts = dbContextServerInfo.server?.split(':') || [];
      const host = serverParts[0] || 'localhost';
      const port = serverParts[1] ? parseInt(serverParts[1]) : 3306;
      
      setConnectionInfo({
        server: host,
        port: port,
        database: dbContextServerInfo.database || '',
        username: dbContextServerInfo.username || '',
      });
    }
  }, [dbContextConnected, dbContextServerInfo]);
  
  // DBConnectionContext가 업데이트되면 로컬 상태도 업데이트
  useEffect(() => {
    if (dbContextConnected !== isDbConnected) {
      setIsDbConnected(dbContextConnected);
    }
    if (dbContextServerInfo !== dbServerInfo) {
      setDbServerInfo(dbContextServerInfo);
    }
  }, [dbContextConnected, dbContextServerInfo, isDbConnected, dbServerInfo]);

  // MySQL 연결 상태 확인 - DBConnectionContext를 우선 사용하고, 필요시에만 health check 수행
  useEffect(() => {
    // DBConnectionContext의 상태가 있으면 그것을 우선 사용 (중복 health check 방지)
    if (dbContextConnected !== undefined && dbContextServerInfo !== undefined) {
      // DBConnectionContext가 이미 health check를 수행하므로 여기서는 동기화만 수행
      return;
    }
    
    // DBConnectionContext가 없을 때만 직접 health check 수행
    const checkDbStatus = () => {
      try {
        const userDisconnectedState = localStorage.getItem(DB_CONNECTION_TOGGLE_KEY) === 'false';
        
        if (userDisconnectedState) {
          setIsDbConnected(false);
          setDbServerInfo(null);
          return;
        }
      } catch {
        // localStorage 접근 실패 시 무시
      }

      // 실제 health check 수행
      const checkDbHealth = async () => {
        try {
          const response = await axios.get(`/health`);
          const healthData = response.data;
          const connected = healthData.database?.connected === true || healthData.database === 'connected';
          setIsDbConnected(connected);
          
          if (connected && healthData.database && healthData.database.database) {
            const info = {
              server: healthData.database.host || 'localhost',
              port: healthData.database.port || 3306,
              database: healthData.database.database || '',
              username: healthData.database.user || '',
            };
            setDbServerInfo(info);
            setConnectionInfo(info);
          } else {
            setDbServerInfo(null);
            setConnectionInfo({
              server: '',
              database: '',
              username: '',
            });
          }
        } catch (error) {
          setIsDbConnected(false);
          setDbServerInfo(null);
          setConnectionInfo({
            server: '',
            database: '',
            username: '',
          });
        }
      };
      
      checkDbHealth();
    };
    
    // 초기 health check 실행 (DBConnectionContext가 없을 때만)
    checkDbStatus();
    
    // 주기적으로 백엔드 상태 확인 (5분마다 - 빈도 줄임)
    const healthInterval = setInterval(checkDbStatus, 300000);
    
    // localStorage 변경 감지
    const handleStorageChange = (e) => {
      if (e.key === DB_CONNECTION_TOGGLE_KEY) {
        checkDbStatus();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // 같은 창에서 localStorage 변경 감지 (CustomEvent 사용)
    const handleCustomStorageChange = () => {
      checkDbStatus();
    };
    window.addEventListener('dbConnectionToggleChanged', handleCustomStorageChange);
    
    return () => {
      clearInterval(healthInterval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dbConnectionToggleChanged', handleCustomStorageChange);
    };
  }, [dbContextConnected, dbContextServerInfo]); // DBConnectionContext 상태를 의존성에 추가

  // 통계 계산
  const failedTasks = tasks.filter(t => t.status === 'failed').length;
  const runningTasks = tasks.filter(t => t.status === 'running');

  return (
    <>
      {/* StatusBar - 항상 최상위에 표시 */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '32px',
        backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5',
        borderTop: `1px solid ${isDark ? '#333' : '#d9d9d9'}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        fontSize: '12px',
        color: isDark ? '#B0B0B0' : '#4a4a4a',
        zIndex: 1300,
        fontFamily: "'Noto Sans KR', sans-serif"
      }}>
        {/* 좌측: V3.0 + 프로젝트 정보 + 시스템 상태 + DB */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '2px 8px',
            backgroundColor: isDark ? '#1a2a3a' : '#e6f7ff',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            color: '#00BFFF'
          }}>
            {t('statusBar.version')}
          </div>

          <div style={{ width: '1px', height: '16px', backgroundColor: isDark ? '#333' : '#d9d9d9' }} />

          {/* 현재 프로젝트 정보 */}
          {currentProject && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FiFolder size={12} color={isDark ? '#B0B0B0' : '#4a4a4a'} />
                <span style={{ fontSize: '11px', color: isDark ? '#B0B0B0' : '#4a4a4a' }}>
                  {currentProject.name}
                </span>
              </div>
              <div style={{ width: '1px', height: '16px', backgroundColor: isDark ? '#333' : '#d9d9d9' }} />
            </>
          )}

          {/* 신호등 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '3px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: failedTasks > 0 ? '#FF5252' : (isDark ? '#333' : '#ddd'),
                boxShadow: failedTasks > 0 ? '0 0 6px rgba(255, 82, 82, 0.6)' : 'none',
                transition: 'all 0.3s'
              }} title="에러" />
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: runningTasks.length > 0 ? '#FFA726' : (isDark ? '#333' : '#ddd'),
                boxShadow: runningTasks.length > 0 ? '0 0 6px rgba(255, 167, 38, 0.6)' : 'none',
                transition: 'all 0.3s'
              }} title="작업 중" />
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: runningTasks.length === 0 && failedTasks === 0 ? '#66BB6A' : (isDark ? '#333' : '#ddd'),
                boxShadow: runningTasks.length === 0 && failedTasks === 0 ? '0 0 6px rgba(102, 187, 106, 0.6)' : 'none',
                transition: 'all 0.3s'
              }} title="정상" />
            </div>
            <span>{t('statusBar.systemStatus')}</span>
          </div>

          <div style={{ width: '1px', height: '16px', backgroundColor: isDark ? '#333' : '#d9d9d9' }} />

          {/* DB 상태 - MySQL 연결 상태 및 DB 이름 표시 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isDbConnected ? (
              <>
                <FiDatabase size={14} color="#66BB6A" />
                <span style={{ color: '#66BB6A', fontWeight: 500 }}>
                  MySQL 연결됨
                </span>
                {(connectionInfo.database || dbServerInfo?.database) && (
                  <span style={{ 
                    color: isDark ? '#B0B0B0' : '#4a4a4a',
                    fontSize: '11px',
                    fontWeight: 400,
                    marginLeft: '4px'
                  }}>
                    (DB: {(connectionInfo.database || dbServerInfo?.database)})
                  </span>
                )}
                {(connectionInfo.server || dbServerInfo?.server) && (
                  <span style={{ 
                    color: isDark ? '#888' : '#666',
                    fontSize: '10px',
                    fontWeight: 400,
                    marginLeft: '2px'
                  }}>
                    @{connectionInfo.server || dbServerInfo?.server}
                  </span>
                )}
              </>
            ) : (
              <>
                <FiX size={14} color="#FF4C61" />
                <span style={{ color: '#FF4C61', fontWeight: 500 }}>MySQL 연결 해제</span>
              </>
            )}
          </div>
        </div>

        {/* 우측: 빈 공간 (접힘/펼침 메뉴 제거) */}
        <div style={{ flex: 1 }} />
      </div>
    </>
  );
}

export default StatusBar;

